import { cn } from '@nexelem/utils';
import { DayPicker, getDefaultClassNames, Locale } from 'react-day-picker';
import { useState } from 'react';
import { isHoverRangeMiddle, isHoverRangeStart, isHoverRangeEnd } from './isHoverRange';
import { isWeekend } from './isWeekend';
import { useDateFNSConfig } from '@nexelem/config';
import { t } from 'i18next';
import { CalendarButton } from './CalendarButton';
import { WeekNumberButton } from './WeekNumberButton';
import { IconButton } from '../IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { addYears, endOfYear, startOfYear } from 'date-fns';
import { DropdownNav } from './DropdownNav';
import { MonthsDropdown } from './MonthsDropdown';
import { YearsDropdown } from './YearsDropdown';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export const Calendar = ({ numberOfMonths = 1, ...props }: CalendarProps) => {
  const defaultClassNames = getDefaultClassNames();
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const { locale } = useDateFNSConfig();

  const startMonth = startOfYear(addYears(new Date(), -2));
  const endMonth = endOfYear(addYears(new Date(), 2));

  return (
    <DayPicker
      endMonth={endMonth}
      locale={props.locale || (locale as Locale)}
      navLayout={numberOfMonths === 1 ? 'after' : 'around'}
      numberOfMonths={numberOfMonths}
      showOutsideDays={numberOfMonths === 1}
      startMonth={startMonth}
      classNames={{
        root: cn('flex flex-col gap-2', defaultClassNames.root),
        months: cn('flex gap-4', defaultClassNames.months),
        caption_label: cn('w-full text-t16-sb text-text-action capitalize', defaultClassNames.caption_label),
        week: cn(
          '[&:last-child_td:last-child]:rounded-br-sm [&:last-child_td:last-child]:border-b [&:last-child_td:nth-last-child(2)]:rounded-bl-sm [&:last-child_td:nth-last-child(2)]:border-b',
          defaultClassNames.week,
        ),
        month_grid: cn('border-spacing-0', defaultClassNames.month_grid),
        weekday: cn(
          'h-12 w-12 text-s11-sb text-text-secondary uppercase',
          'box-border border-border-container last:rounded-tr-sm last:border-t last:border-r last:bg-surface-container-lighter nth-last-2:rounded-tl-sm nth-last-2:border-t nth-last-2:border-l nth-last-2:bg-surface-container-lighter',
          defaultClassNames.weekday,
        ),
        day: cn('box-border h-12 w-12 max-w-12 text-center text-s16 text-text-action', defaultClassNames.day),
        today: cn(
          '[&_.active-button]:rounded-full [&_.active-button]:border-2 [&_.active-button]:border-icon-brand',
          defaultClassNames.today,
        ),
        selected: cn(
          '[&_.active-button]:bg-surface-focus [&_.active-button]:text-s16-sb [&_.active-button]:text-text-primary-inverse [&:hover_.active-button]:bg-icon-hover!',
          defaultClassNames.selected,
        ),
        outside: cn('[&_button]:text-text-disabled', defaultClassNames.outside),
        range_start: cn(
          '[&_.active-button]:mr-2 [&_.highlight-button]:ml-2 [&_.highlight-button]:rounded-l-full [&_.highlight-button]:bg-surface-hover-lighter',
          defaultClassNames.range_start,
        ),
        range_end: cn(
          '[&_.active-button]:ml-2 [&_.highlight-button]:mr-2 [&_.highlight-button]:rounded-r-full [&_.highlight-button]:bg-surface-hover-lighter',
          defaultClassNames.range_end,
        ),
        range_middle: cn(
          '[&_.highlight-button]:bg-surface-hover-lighter',
          '[&_.active-button]:bg-transparent! [&_.active-button]:text-s16! [&_.active-button]:text-text-action! [&:hover_.active-button]:bg-surface-hover-lighter!',
          defaultClassNames.range_middle,
        ),
        disabled: cn('[&_button]:cursor-auto [&_button]:text-text-disabled', defaultClassNames.disabled),
        week_number: cn(
          'box-border h-12 w-12 border-x border-border-container bg-surface-container text-s16-sb text-text-action last:rounded-b-sm [.rdp-weeks_tr:last-child_&]:rounded-b-sm [.rdp-weeks_tr:last-child_&]:border-b',
          defaultClassNames.week_number,
        ),
        nav: cn('absolute top-0 right-0', defaultClassNames.nav),
        month_caption: cn(
          'mb-2 inline-flex h-11 items-center',
          numberOfMonths !== 1 && 'absolute left-[50%] translate-x-[-50%]',
          defaultClassNames.month_caption,
        ),
        month: cn('relative', numberOfMonths !== 1 && 'flex flex-col', defaultClassNames.month),
        button_next: cn('mb-2 self-end', defaultClassNames.button_next),
        button_previous: cn('mb-2', defaultClassNames.button_previous),
      }}
      components={{
        DropdownNav,
        MonthsDropdown,
        YearsDropdown,
        PreviousMonthButton: ({ children: _, ...props }) => (
          <IconButton Icon={<FontAwesomeIcon icon={faChevronLeft} />} {...props} />
        ),
        NextMonthButton: ({ children: _, ...props }) => (
          <IconButton Icon={<FontAwesomeIcon icon={faChevronRight} />} {...props} />
        ),
        WeekNumber: WeekNumberButton,
        DayButton: CalendarButton,
        WeekNumberHeader: () => (
          <td className="box-border flex h-12 w-12 items-center justify-center rounded-t-sm border-x border-t border-border-container bg-surface-container text-s11-sb text-text-secondary uppercase">
            {t('common.week')}
          </td>
        ),
      }}
      modifiers={{
        weekend: isWeekend,
        hoverRangeMiddle:
          props.mode === 'range'
            ? isHoverRangeMiddle({ selected: props.selected, max: props.max, hoveredDay })
            : undefined,
        hoverRangeStart:
          props.mode === 'range'
            ? isHoverRangeStart({ selected: props.selected, max: props.max, hoveredDay })
            : undefined,
        hoverRangeEnd:
          props.mode === 'range'
            ? isHoverRangeEnd({ selected: props.selected, max: props.max, hoveredDay })
            : undefined,
      }}
      modifiersClassNames={{
        weekend: cn('border-border-container bg-surface-container-lighter last:border-r nth-last-2:border-l'),
        hoverRangeStart: cn(
          '[&_.active-button]:mr-1 [&_.highlight-button]:ml-1 [&_.highlight-button]:rounded-l-full [&_.highlight-button]:bg-surface-hover-lighter',
        ),
        hoverRangeEnd: cn(
          '[&_.active-button]:ml-1 [&_.highlight-button]:mr-1 [&_.highlight-button]:rounded-r-full [&_.highlight-button]:bg-surface-hover-lighter',
        ),
        hoverRangeMiddle: cn('[&_.highlight-button]:bg-surface-hover-lighter'),
      }}
      onDayMouseEnter={(day) => setHoveredDay(day)}
      onDayMouseLeave={() => setHoveredDay(null)}
      {...props}
    />
  );
};
